import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getCrawlingConfig,
  saveCrawlingConfig,
  getCrawlingSchedules,
  createCrawlingSchedule,
  updateCrawlingSchedule,
  deleteCrawlingSchedule,
  toggleCrawlingSchedule,
} from '../../lib/api/admin-crawling-api';
import SyncSchedulePanel from './KbCrawlingPanel';
import './CrawlingManagement.css';

const EXCEPT_TYPES = [
  { value: 'EXCEPT_1', label: '1층 제외' },
  { value: 'EXCEPT_3', label: '저층(1~3) 제외' },
  { value: 'ALL', label: '전체' },
];
const PRICE_TYPES = [
  { value: 'LOWER', label: '최저가' },
  { value: 'LOWER_2', label: '최저가(2번째)' },
  { value: 'MID', label: '중간가' },
  { value: 'TOP', label: '최고가' },
];

const DEFAULT_CONFIG = {
  configName: '',
  topPriceDtFrom: '', topPriceDtTo: '',
  lowPriceDtFrom: '', lowPriceDtTo: '',
  silMCntDateFrom: '', silMCntDateTo: '',
  silJCntDateFrom: '', silJCntDateTo: '',
  a1ExceptType: 'EXCEPT_3', a1PriceType: 'LOWER',
  b1ExceptType: 'ALL', b1PriceType: 'LOWER',
  kbTradeApply: false, kbLeaseApply: false,
  stdPremium: 0,
  tradeFilterKeywords: '', leaseFilterKeywords: '',
  excludeRealtorAssoc: false,
  extractTopPrice: true, extractRealTrade: true, extractCurrentInfo: true,
  extractBunyang: true, extractArticleStatus: true,
  extractLinks: true, extractComplexInfo: false,
};

// 요일: cron dow(0=일 ~ 6=토)
const WEEKDAYS = [
  { v: 1, l: '월' }, { v: 2, l: '화' }, { v: 3, l: '수' }, { v: 4, l: '목' },
  { v: 5, l: '금' }, { v: 6, l: '토' }, { v: 0, l: '일' },
];

// cron식 "mm hh * * dow" ↔ 요일/시간 상호 변환 (사용자에게는 cron식을 숨김)
const cronToWeekly = (cron) => {
  const parts = String(cron || '').trim().split(/\s+/);
  const pad = (n) => String(n).padStart(2, '0');
  if (parts.length !== 5) return { days: [1], time: '06:00' };
  const mm = Number(parts[0]) || 0;
  const hh = Number(parts[1]) || 0;
  const dow = parts[4];
  let days;
  if (dow === '*' || dow === '') days = [0, 1, 2, 3, 4, 5, 6];
  else days = dow.split(',').map((x) => Number(x.trim())).filter((n) => Number.isFinite(n)).map((n) => (n === 7 ? 0 : n));
  return { days, time: `${pad(hh)}:${pad(mm)}` };
};
const weeklyToCron = (days, time) => {
  const [hh, mm] = String(time || '06:00').split(':').map((x) => Number(x) || 0);
  const uniq = Array.from(new Set((days || []).map((n) => (n === 7 ? 0 : n)))).sort((a, b) => a - b);
  const dowField = (uniq.length === 0 || uniq.length === 7) ? '*' : uniq.join(',');
  return `${mm} ${hh} * * ${dowField}`;
};
const weeklyLabel = (cron) => {
  const { days, time } = cronToWeekly(cron);
  if (days.length === 7) return `매일 ${time}`;
  const order = [1, 2, 3, 4, 5, 6, 0];
  const label = order.filter((d) => days.includes(d)).map((d) => WEEKDAYS.find((w) => w.v === d)?.l).join(',');
  return `매주 ${label || '-'} ${time}`;
};

const DEFAULT_SCHEDULE = {
  id: null,
  scheduleName: '',
  configId: '',
  scheduleType: 'CRON',
  weekDays: [1],
  weekTime: '06:00',
  runAt: '',
  intervalMinutes: 60,
  timezone: 'Asia/Seoul',
  targetType: 'ALL_RANKER',
  targetBjdCode: '',
  targetDanji: '',
  jobNamePrefix: '스케줄',
  isEnabled: true,
};

const isoToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fmtTs = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function CrawlingManagement() {
  const { token } = useAuth();
  const [mainTab, setMainTab] = useState('naver'); // naver | kb
  const [subTab, setSubTab] = useState('config');

  return (
    <div className="crawling-management">
      {/* 상위 탭: 네이버크롤링 | KB | 실거래 | 공급 */}
      <div className="crawling-subtabs" style={{ marginBottom: 12 }}>
        <button className={`crawling-subtab ${mainTab === 'naver' ? 'active' : ''}`} onClick={() => setMainTab('naver')}>네이버크롤링</button>
        <button className={`crawling-subtab ${mainTab === 'KB' ? 'active' : ''}`} onClick={() => setMainTab('KB')}>KB</button>
        <button className={`crawling-subtab ${mainTab === 'REAL' ? 'active' : ''}`} onClick={() => setMainTab('REAL')}>실거래</button>
        <button className={`crawling-subtab ${mainTab === 'SUPPLY' ? 'active' : ''}`} onClick={() => setMainTab('SUPPLY')}>공급</button>
      </div>

      {mainTab === 'naver' ? (
        <>
          <p className="crawling-note-warn">
            ※ 매매(또는 전세) 물건이 없으면 해당 매매가·전세가는 KB 일반평균가로 지정됩니다. (KB시세 적용 체크 여부와 무관하게 대체)
          </p>
          <div className="crawling-subtabs">
            <button className={`crawling-subtab ${subTab === 'config' ? 'active' : ''}`} onClick={() => setSubTab('config')}>크롤링 설정</button>
            <button className={`crawling-subtab ${subTab === 'schedule' ? 'active' : ''}`} onClick={() => setSubTab('schedule')}>스케줄</button>
          </div>
          {subTab === 'config' ? <ConfigPanel token={token} /> : <SchedulePanel token={token} />}
        </>
      ) : (
        <SyncSchedulePanel category={mainTab} />
      )}
    </div>
  );
}

// ============================ 설정 ============================
function ConfigPanel({ token }) {
  const [form, setForm] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const data = await getCrawlingConfig(token);
      setForm({ ...DEFAULT_CONFIG, ...(data || {}) });
    } catch (e) {
      setErr(e?.message || '설정 조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true); setMsg(''); setErr('');
    try {
      const payload = {
        ...form,
        stdPremium: Number(form.stdPremium) || 0,
        extractRealTrade: true,
        extractCurrentInfo: true,
      };
      const saved = await saveCrawlingConfig(token, payload);
      setForm({ ...DEFAULT_CONFIG, ...(saved || {}) });
      setMsg('설정이 저장되었습니다.');
    } catch (e) {
      setErr(e?.message || '설정 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const dateRange = (label, fromField, toField) => (
    <div className="crawling-field">
      <label className="crawling-label">{label}</label>
      <div className="crawling-daterange">
        <input type="date" value={form[fromField] || ''} onChange={(e) => set(fromField, e.target.value)} />
        <span>~</span>
        <input type="date" value={form[toField] || ''} onChange={(e) => set(toField, e.target.value)} />
      </div>
    </div>
  );

  const selectField = (label, field, options) => (
    <div className="crawling-field">
      <label className="crawling-label">{label}</label>
      <select value={form[field]} onChange={(e) => set(field, e.target.value)}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  const check = (label, field, disabled = false) => (
    <label className={`crawling-check ${disabled ? 'disabled' : ''}`}>
      <input type="checkbox" checked={!!form[field]} disabled={disabled} onChange={(e) => set(field, e.target.checked)} />
      <span>{label}</span>
    </label>
  );

  if (loading) return <div className="crawling-status">불러오는 중...</div>;

  return (
    <div className="crawling-form">
      {err && <div className="crawling-status error">{err}</div>}
      {msg && <div className="crawling-status ok">{msg}</div>}

      <section className="crawling-section">
        <h4>추출 항목</h4>
        <div className="crawling-checks">
          {check('최고가', 'extractTopPrice')}
          {check('실거래(필수)', 'extractRealTrade', true)}
          {check('현재정보(필수)', 'extractCurrentInfo', true)}
          {check('분양권', 'extractBunyang')}
          {check('매물현황', 'extractArticleStatus')}
          {check('링크(내부)', 'extractLinks')}
          {check('단지정보(내부)', 'extractComplexInfo')}
        </div>
      </section>

      <div className="crawling-cols">
        <section className="crawling-section">
          <h4>기간 설정</h4>
          <div className="crawling-grid one">
            {dateRange('실거래 최고가 기간', 'topPriceDtFrom', 'topPriceDtTo')}
            {dateRange('실거래 최저가 기간', 'lowPriceDtFrom', 'lowPriceDtTo')}
            {dateRange('평균가 산정 기간', 'silMCntDateFrom', 'silMCntDateTo')}
            {dateRange('건수 산정 기간', 'silJCntDateFrom', 'silJCntDateTo')}
          </div>
        </section>

        <section className="crawling-section">
          <h4>매매 / 전세 조건</h4>
          <div className="crawling-grid">
            {selectField('매매 층 조건', 'a1ExceptType', EXCEPT_TYPES)}
            {selectField('매매 가격 기준', 'a1PriceType', PRICE_TYPES)}
            {selectField('전세 층 조건', 'b1ExceptType', EXCEPT_TYPES)}
            {selectField('전세 가격 기준', 'b1PriceType', PRICE_TYPES)}
            <div className="crawling-field">
              <label className="crawling-label">최종 매매가 KB시세 적용</label>
              <label className="crawling-check">
                <input type="checkbox" checked={!!form.kbTradeApply} onChange={(e) => set('kbTradeApply', e.target.checked)} />
                <span>적용</span>
              </label>
            </div>
            <div className="crawling-field">
              <label className="crawling-label">최종 전세가 KB시세 적용</label>
              <label className="crawling-check">
                <input type="checkbox" checked={!!form.kbLeaseApply} onChange={(e) => set('kbLeaseApply', e.target.checked)} />
                <span>적용</span>
              </label>
            </div>
          </div>
        </section>
      </div>

      <section className="crawling-section">
        <h4>기타</h4>
        <div className="crawling-grid four">
          <div className="crawling-field">
            <label className="crawling-label">프리미엄 기준값(만원)</label>
            <input type="number" value={form.stdPremium} onChange={(e) => set('stdPremium', e.target.value)} />
          </div>
          <div className="crawling-field">
            <label className="crawling-label">설정 이름(선택)</label>
            <input type="text" value={form.configName || ''} onChange={(e) => set('configName', e.target.value)} placeholder="예) 기본설정" />
          </div>
          <div className="crawling-field">
            <label className="crawling-label">매매 제외 키워드(콤마)</label>
            <input type="text" value={form.tradeFilterKeywords || ''} onChange={(e) => set('tradeFilterKeywords', e.target.value)} placeholder="예) 급매,협의" />
          </div>
          <div className="crawling-field">
            <label className="crawling-label">전세 제외 키워드(콤마)</label>
            <input type="text" value={form.leaseFilterKeywords || ''} onChange={(e) => set('leaseFilterKeywords', e.target.value)} placeholder="예) 반전세" />
          </div>
        </div>
        <div style={{ marginTop: 6 }}>
          {check('공인중개사협회 매물 제외', 'excludeRealtorAssoc')}
        </div>
      </section>

      <div className="crawling-actions">
        <button className="crawling-btn" onClick={load} disabled={saving}>새로고침</button>
        <button className="crawling-btn primary" onClick={handleSave} disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
      </div>
    </div>
  );
}

// ============================ 스케줄 ============================
function SchedulePanel({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(null); // schedule draft or null

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      setItems(await getCrawlingSchedules(token));
    } catch (e) {
      setErr(e?.message || '스케줄 조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditing({ ...DEFAULT_SCHEDULE });
  const openEdit = (s) => {
    const weekly = cronToWeekly(s.cronExpr);
    setEditing({
      ...DEFAULT_SCHEDULE,
      ...s,
      configId: s.configId ?? '',
      weekDays: weekly.days,
      weekTime: weekly.time,
      runAt: isoToLocalInput(s.runAt),
      intervalMinutes: s.intervalMinutes ?? 60,
      targetDanji: s.targetDanji || '',
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('이 스케줄을 삭제하시겠습니까?')) return;
    try { await deleteCrawlingSchedule(token, id); await load(); }
    catch (e) { alert(e?.message || '삭제에 실패했습니다.'); }
  };
  const handleToggle = async (id) => {
    try { await toggleCrawlingSchedule(token, id); await load(); }
    catch (e) { alert(e?.message || '상태 변경에 실패했습니다.'); }
  };

  const scheduleDetail = (s) => {
    if (s.scheduleType === 'CRON') return weeklyLabel(s.cronExpr);
    if (s.scheduleType === 'ONCE') return `1회 · ${fmtTs(s.runAt)}`;
    if (s.scheduleType === 'INTERVAL') return `주기 · ${s.intervalMinutes}분`;
    return s.scheduleType;
  };
  const targetDetail = (s) => {
    if (s.targetType === 'ALL_RANKER') return '전체 랭커';
    if (s.targetType === 'REGION') return `지역 · ${s.targetBjdCode || '-'}`;
    if (s.targetType === 'CUSTOM') return '직접 지정';
    return s.targetType;
  };

  return (
    <div className="crawling-schedule">
      {err && <div className="crawling-status error">{err}</div>}
      <div className="crawling-actions" style={{ justifyContent: 'space-between' }}>
        <button className="crawling-btn" onClick={load}>새로고침</button>
        <button className="crawling-btn primary" onClick={openNew}>+ 스케줄 추가</button>
      </div>

      <table className="admin-basic-table crawling-table">
        <thead>
          <tr>
            <th>이름</th><th>주기</th><th>대상</th><th>사용</th><th>다음 실행</th><th>최근 실행/상태</th><th>관리</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td className="admin-empty-row" colSpan={7}>불러오는 중...</td></tr>
          ) : items.length === 0 ? (
            <tr><td className="admin-empty-row" colSpan={7}>등록된 스케줄이 없습니다.</td></tr>
          ) : items.map((s) => (
            <tr key={s.id}>
              <td>{s.scheduleName}</td>
              <td>{scheduleDetail(s)}</td>
              <td>{targetDetail(s)}</td>
              <td>
                <button className={`crawling-toggle ${s.isEnabled ? 'on' : 'off'}`} onClick={() => handleToggle(s.id)}>
                  {s.isEnabled ? 'ON' : 'OFF'}
                </button>
              </td>
              <td>{fmtTs(s.nextRunAt)}</td>
              <td>{fmtTs(s.lastRunAt)}{s.lastStatus ? ` (${s.lastStatus})` : ''}</td>
              <td>
                <button className="crawling-btn sm" onClick={() => openEdit(s)}>수정</button>
                <button className="crawling-btn sm danger" onClick={() => handleDelete(s.id)}>삭제</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <ScheduleEditor
          token={token}
          draft={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function ScheduleEditor({ token, draft, onClose, onSaved }) {
  const [form, setForm] = useState(draft);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true); setErr('');
    try {
      const payload = {
        scheduleName: (form.scheduleName || '').trim(),
        configId: form.configId === '' || form.configId == null ? null : Number(form.configId),
        scheduleType: form.scheduleType,
        cronExpr: form.scheduleType === 'CRON' ? weeklyToCron(form.weekDays, form.weekTime) : null,
        runAt: form.scheduleType === 'ONCE' ? (form.runAt || null) : null,
        intervalMinutes: form.scheduleType === 'INTERVAL' ? (Number(form.intervalMinutes) || null) : null,
        timezone: (form.timezone || 'Asia/Seoul').trim(),
        targetType: form.targetType,
        targetBjdCode: form.targetType === 'REGION' ? (form.targetBjdCode || '').trim() : null,
        targetDanji: form.targetType === 'CUSTOM' ? (form.targetDanji || '').trim() || null : null,
        jobNamePrefix: (form.jobNamePrefix || '스케줄').trim(),
        isEnabled: !!form.isEnabled,
      };
      if (!payload.scheduleName) { setErr('스케줄 이름을 입력해 주세요.'); setSaving(false); return; }
      if (form.scheduleType === 'CRON' && (!form.weekDays || form.weekDays.length === 0)) {
        setErr('요일을 하나 이상 선택해 주세요.'); setSaving(false); return;
      }
      if (payload.targetType === 'CUSTOM' && payload.targetDanji) {
        try { JSON.parse(payload.targetDanji); }
        catch { setErr('직접 지정 대상은 올바른 JSON 배열이어야 합니다.'); setSaving(false); return; }
      }
      if (form.id) await updateCrawlingSchedule(token, form.id, payload);
      else await createCrawlingSchedule(token, payload);
      await onSaved();
    } catch (e) {
      setErr(e?.message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="crawling-modal-overlay" onClick={onClose}>
      <div className="crawling-modal" onClick={(e) => e.stopPropagation()}>
        <div className="crawling-modal-head">
          <strong>{form.id ? '스케줄 수정' : '스케줄 추가'}</strong>
          <button className="crawling-modal-x" onClick={onClose}>×</button>
        </div>
        <div className="crawling-modal-body">
          {err && <div className="crawling-status error">{err}</div>}

          <div className="crawling-field">
            <label className="crawling-label">스케줄 이름</label>
            <input type="text" value={form.scheduleName} onChange={(e) => set('scheduleName', e.target.value)} placeholder="예) 매주 월요일 06시" />
          </div>

          <div className="crawling-field">
            <label className="crawling-label">사용 설정(config_id, 선택)</label>
            <input type="number" value={form.configId} onChange={(e) => set('configId', e.target.value)} placeholder="비우면 활성 설정 사용" />
          </div>

          <div className="crawling-field">
            <label className="crawling-label">주기 유형</label>
            <select value={form.scheduleType} onChange={(e) => set('scheduleType', e.target.value)}>
              <option value="CRON">매주(요일+시간)</option>
              <option value="ONCE">1회(날짜/시간)</option>
              <option value="INTERVAL">주기(분 간격)</option>
            </select>
          </div>

          {form.scheduleType === 'CRON' && (
            <div className="crawling-field">
              <label className="crawling-label">요일 · 시간</label>
              <div className="crawling-weekdays">
                {WEEKDAYS.map((d) => (
                  <button
                    type="button"
                    key={d.v}
                    className={`crawling-day ${(form.weekDays || []).includes(d.v) ? 'on' : ''}`}
                    onClick={() => set('weekDays', (form.weekDays || []).includes(d.v)
                      ? form.weekDays.filter((x) => x !== d.v)
                      : [...(form.weekDays || []), d.v])}
                  >
                    {d.l}
                  </button>
                ))}
                <input className="crawling-day-time" type="time" value={form.weekTime} onChange={(e) => set('weekTime', e.target.value)} />
              </div>
              <div className="crawling-weekday-presets">
                <button type="button" onClick={() => set('weekDays', [1, 2, 3, 4, 5])}>평일</button>
                <button type="button" onClick={() => set('weekDays', [0, 6])}>주말</button>
                <button type="button" onClick={() => set('weekDays', [0, 1, 2, 3, 4, 5, 6])}>매일</button>
                <span className="crawling-hint">{weeklyLabel(weeklyToCron(form.weekDays, form.weekTime))}</span>
              </div>
            </div>
          )}
          {form.scheduleType === 'ONCE' && (
            <div className="crawling-field">
              <label className="crawling-label">실행 시각</label>
              <input type="datetime-local" value={form.runAt} onChange={(e) => set('runAt', e.target.value)} />
            </div>
          )}
          {form.scheduleType === 'INTERVAL' && (
            <div className="crawling-field">
              <label className="crawling-label">주기(분)</label>
              <input type="number" value={form.intervalMinutes} onChange={(e) => set('intervalMinutes', e.target.value)} min={1} />
            </div>
          )}

          <div className="crawling-field">
            <label className="crawling-label">타임존</label>
            <input type="text" value={form.timezone} onChange={(e) => set('timezone', e.target.value)} placeholder="Asia/Seoul" />
          </div>

          <div className="crawling-field">
            <label className="crawling-label">대상 유형</label>
            <select value={form.targetType} onChange={(e) => set('targetType', e.target.value)}>
              <option value="ALL_RANKER">전체 랭커</option>
              <option value="REGION">지역(법정동코드 하위)</option>
              <option value="CUSTOM">직접 지정</option>
            </select>
          </div>
          {form.targetType === 'REGION' && (
            <div className="crawling-field">
              <label className="crawling-label">법정동코드</label>
              <input type="text" value={form.targetBjdCode} onChange={(e) => set('targetBjdCode', e.target.value)} placeholder="예) 1168010100" />
            </div>
          )}
          {form.targetType === 'CUSTOM' && (
            <div className="crawling-field">
              <label className="crawling-label">대상 단지(JSON 배열)</label>
              <textarea rows={4} value={form.targetDanji} onChange={(e) => set('targetDanji', e.target.value)}
                placeholder='[{"naver_danji_code":"..","ptp_no":".."}]' />
            </div>
          )}

          <div className="crawling-field">
            <label className="crawling-label">작업명 접두어</label>
            <input type="text" value={form.jobNamePrefix} onChange={(e) => set('jobNamePrefix', e.target.value)} />
          </div>

          <label className="crawling-check">
            <input type="checkbox" checked={!!form.isEnabled} onChange={(e) => set('isEnabled', e.target.checked)} />
            <span>사용(활성)</span>
          </label>
        </div>
        <div className="crawling-modal-foot">
          <button className="crawling-btn" onClick={onClose} disabled={saving}>취소</button>
          <button className="crawling-btn primary" onClick={handleSave} disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
        </div>
      </div>
    </div>
  );
}

export default CrawlingManagement;
